/**
 * Motor de preço por canal. Funções puras: sem banco, sem rede, fáceis de testar.
 *
 * Margem é sempre sobre o PREÇO DE VENDA, depois das taxas do canal:
 *   lucro  = preço − custo − taxas
 *   taxas  = preço × (comissão + outras %) + taxa fixa do canal (se o preço estiver abaixo do limite) + outros fixos
 *   margem = lucro ÷ preço
 * Para chegar numa margem m:
 *   preço = (custo + fixos) ÷ (1 − comissão − outras % − m)
 */

export interface ChannelCosts {
  takeRatePct: number; // comissão do canal (Meli: 16)
  extraPct: number; // outras taxas em % do preço
  extraFixed: number; // outros custos fixos por unidade
  fixedFee: number; // taxa fixa do canal por unidade...
  fixedFeeBelow: number | null; // ...cobrada quando o preço fica abaixo deste valor (null = sempre)
  /** Custo fixo por faixa de preço (substitui fixedFee/fixedFeeBelow): a primeira faixa com preço < below vale. */
  fixedFeeTable?: FeeBand[] | null;
  capAtPmc: boolean;
  rounding: 'none' | '90' | '99';
  minMarginAlertPct: number;
}

export interface FeeBand { below: number; fixed?: number; pctOfPrice?: number }

/** Tabela do Mercado Livre (Brasil) para itens abaixo de R$ 79. */
export const MELI_FIXED_FEE_TABLE: FeeBand[] = [
  { below: 12.5, pctOfPrice: 50 }, { below: 29, fixed: 6.25 }, { below: 50, fixed: 6.5 }, { below: 79, fixed: 6.75 },
];

const cents = (v: number) => Math.round(v * 100) / 100;

/** Custo fixo do canal para um preço. */
export function fixedFeeFor(price: number, c: ChannelCosts): number {
  if (c.fixedFeeTable?.length) {
    const b = c.fixedFeeTable.find((x) => price < x.below);
    return b ? cents(b.fixed ?? (price * (b.pctOfPrice ?? 0)) / 100) : 0;
  }
  return feeApplies(price, c) ? c.fixedFee : 0;
}

/** Arredonda para cima até o final escolhido (,90 ou ,99). */
export function roundPrice(price: number, rounding: ChannelCosts['rounding']): number {
  if (rounding === 'none') return cents(price);
  const end = rounding === '90' ? 0.9 : 0.99;
  const base = Math.floor(price);
  const candidate = base + end;
  return cents(candidate >= price - 1e-9 ? candidate : base + 1 + end);
}

const feeApplies = (price: number, c: ChannelCosts) => c.fixedFee > 0 && (c.fixedFeeBelow == null || price < c.fixedFeeBelow);

export interface PriceEvaluation {
  price: number;
  fees: number; // comissão + outras % + fixos
  profit: number;
  marginPct: number;
}

/** Margem e lucro de um preço qualquer (usado para preço na mão, API e buy box). */
export function evaluatePrice(price: number, cost: number, c: ChannelCosts): PriceEvaluation {
  const pct = (c.takeRatePct + c.extraPct) / 100;
  const fees = price * pct + fixedFeeFor(price, c) + c.extraFixed;
  const profit = price - cost - fees;
  return { price: cents(price), fees: cents(fees), profit: cents(profit), marginPct: price > 0 ? Math.round((profit / price) * 10000) / 100 : 0 };
}

export interface PriceResult extends PriceEvaluation {
  cappedAtPmc: boolean;
}

/**
 * Preço para atingir a margem pedida. Leva em conta a taxa fixa por faixa:
 * se o preço sem a taxa já passa do limite, ela não entra; se só passa com ela, o preço fica no limite.
 */
export function computePrice(cost: number, marginPct: number, c: ChannelCosts, pmc?: number | null): PriceResult | null {
  const divisor = 1 - (c.takeRatePct + c.extraPct + marginPct) / 100;
  if (divisor <= 0 || !(cost > 0)) return null;
  const withoutFee = (cost + c.extraFixed) / divisor;
  let price: number;
  if (c.fixedFeeTable?.length) {
    // Menor preço que atinge a margem: faixa por faixa, do mais barato para o mais caro.
    price = withoutFee;
    let lo = 0;
    for (const b of c.fixedFeeTable) {
      const d = divisor - (b.pctOfPrice ?? 0) / 100;
      const sol = d > 0 ? (cost + c.extraFixed + (b.fixed ?? 0)) / d : Infinity;
      const candidate = Math.max(sol, lo);
      if (candidate < b.below) { price = candidate; break; }
      lo = b.below;
      price = Math.max(withoutFee, lo);
    }
    return finish(price, cost, c, pmc);
  }
  if (c.fixedFee <= 0 || (c.fixedFeeBelow != null && withoutFee >= c.fixedFeeBelow)) price = withoutFee;
  else {
    const withFee = (cost + c.extraFixed + c.fixedFee) / divisor;
    price = c.fixedFeeBelow != null && withFee >= c.fixedFeeBelow ? c.fixedFeeBelow : withFee;
  }
  return finish(price, cost, c, pmc);
}

function finish(raw: number, cost: number, c: ChannelCosts, pmc?: number | null): PriceResult {
  let price = roundPrice(raw, c.rounding);
  let cappedAtPmc = false;
  if (c.capAtPmc && pmc && pmc > 0 && price > pmc) {
    price = cents(pmc);
    cappedAtPmc = true;
  }
  return { ...evaluatePrice(price, cost, c), cappedAtPmc };
}

export type PriceAlert = 'no_cost' | 'below_cost' | 'above_pmc' | 'low_margin' | 'no_price' | 'capped_at_pmc' | 'default_price';

export interface OfferInput {
  cost: number | null;
  pmc: number | null;
  /** Margem que se aplica ao produto (regra de produto > categoria > geral) e de onde veio. */
  margin: { pct: number; from: 'product' | 'category' | 'general' };
  defaultMarginPct: number;
  mode: 'engine' | 'external';
  fixedPrice: number | null;
  fixedReason: 'manual' | 'buybox' | null;
  apiPrice: number | null;
  /** Kit: preço do produto base × unidades − desconto, já arredondado. Vale depois do preço na mão. */
  kitPrice?: number | null;
  /**
   * PMC oficial (CMED/ABCFarma, na alíquota da UF da conta; kit = base × unidades). Avaliado depois de qualquer origem
   * de preço. capAtOfficialPmc: corta no PMC oficial (só quando a regra está em enforce e o disjuntor liberou).
   */
  officialPmc?: number | null;
  capAtOfficialPmc?: boolean;
}

export interface OfferResult {
  computedPrice: number | null; // o que o motor calcularia (mostrado mesmo quando outro preço vale)
  finalPrice: number | null;
  source: 'manual' | 'buybox' | 'engine' | 'api' | 'default' | 'kit' | 'none';
  marginPct: number | null;
  profit: number | null;
  alerts: PriceAlert[];
  trace: Record<string, unknown>;
  /** Preço acima do PMC oficial (antes do corte) e se foi cortado. Fica fora de alerts: em observação o cliente não vê. */
  officialPmc: { value: number; above: boolean; capped: boolean; priceBefore: number | null } | null;
}

/** Decide o preço que vale para um produto num canal e os alertas dele. */
export function resolveOffer(o: OfferInput, c: ChannelCosts): OfferResult {
  const cost = o.cost != null && o.cost > 0 ? o.cost : null;
  const computed = cost ? computePrice(cost, o.margin.pct, c, o.pmc) : null;
  let finalPrice: number | null = null;
  let source: OfferResult['source'] = 'none';
  const alerts: PriceAlert[] = [];

  if (o.fixedPrice != null && o.fixedPrice > 0) {
    finalPrice = o.fixedPrice;
    source = o.fixedReason === 'buybox' ? 'buybox' : 'manual';
  } else if (o.kitPrice != null && o.kitPrice > 0) {
    finalPrice = o.kitPrice;
    source = 'kit';
  } else if (o.mode === 'engine' && computed) {
    finalPrice = computed.price;
    source = 'engine';
    if (computed.cappedAtPmc) alerts.push('capped_at_pmc');
  } else if (o.mode === 'external' && o.apiPrice != null && o.apiPrice > 0) {
    finalPrice = o.apiPrice;
    source = 'api';
  } else if (cost) {
    // Sem preço de nenhuma origem: custo + taxas do canal + margem padrão.
    const d = computePrice(cost, o.defaultMarginPct, c, o.pmc);
    if (d) {
      finalPrice = d.price;
      source = 'default';
      alerts.push('default_price');
    }
  }

  // PMC oficial: depois de qualquer origem (fixo, buy box, kit, margem, API do ERP, padrão).
  let official: OfferResult['officialPmc'] = null;
  if (o.officialPmc != null && o.officialPmc > 0) {
    const above = finalPrice != null && finalPrice > o.officialPmc + 0.004;
    const capped = above && !!o.capAtOfficialPmc;
    official = { value: o.officialPmc, above, capped, priceBefore: finalPrice };
    if (capped) finalPrice = cents(o.officialPmc);
  }

  if (!cost) alerts.push('no_cost');
  if (finalPrice == null) alerts.push('no_price');
  const ev = finalPrice != null && cost ? evaluatePrice(finalPrice, cost, c) : null;
  if (finalPrice != null && cost && finalPrice < cost) alerts.push('below_cost');
  if (finalPrice != null && o.pmc && o.pmc > 0 && finalPrice > o.pmc + 0.004) alerts.push('above_pmc');
  if (ev && ev.marginPct < c.minMarginAlertPct) alerts.push('low_margin');

  return {
    computedPrice: computed?.price ?? null,
    finalPrice,
    source,
    marginPct: ev?.marginPct ?? null,
    profit: ev?.profit ?? null,
    alerts,
    trace: {
      cost,
      pmc: o.pmc,
      margin: o.margin,
      takeRatePct: c.takeRatePct,
      extraPct: c.extraPct,
      fixedFee: c.fixedFee,
      fixedFeeBelow: c.fixedFeeBelow,
      extraFixed: c.extraFixed,
      fees: ev?.fees ?? null,
      cappedAtPmc: computed?.cappedAtPmc ?? false,
      ...(official ? { officialPmc: official } : {}),
    },
    officialPmc: official,
  };
}
